import React, { useState, useEffect } from 'react';
import { Mail, Twitter, Linkedin, Youtube, Globe, Instagram, MessageCircle } from 'lucide-react';
import { getTranslations } from '../translations';

const Footer = ({ currentPage, setCurrentPage, language = 'en' }) => {
  const t = getTranslations('footer', language);

  const gold = '#C8A96E';
  const charcoal = '#1A1916';
  const secondary = '#6B6862';
  const tertiary = '#9A9690';
  const borderRest = '#E5E3DE';

  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  const handleContactClick = () => {
    if (setCurrentPage) {
      setCurrentPage('contact');
    }
  };

  const quickLinks = [
    { name: t?.links?.home || 'Home', slug: 'home' },
    { name: t?.links?.about || 'About', slug: 'about' },
    { name: t?.links?.infinity || 'Infinity', slug: 'infinity' },
    { name: t?.links?.writings || 'Writings', slug: 'writings' },
    { name: t?.links?.media || 'Media', slug: 'media' },
    { name: t?.links?.contact || 'Contact', slug: 'contact' },
  ];

  return (
    <footer className="py-16 px-6 bg-white" style={{ borderTop: `1px solid ${borderRest}` }}>
      <div className="max-w-6xl mx-auto">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          
          {/* Brand Section */}
          <div>
            <div className="flex items-center space-x-2.5 sm:space-x-3 mb-4">
              <img
                src="Icon.png"
                alt="Zeus Hernandez Logo"
                className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0"
              />
              <h3 className="text-sm font-medium uppercase" style={{ color: charcoal, letterSpacing: '0.16em' }}>Zeus Hernandez</h3>
            </div>
            <p className="text-sm font-light mb-6" style={{ color: secondary, lineHeight: '1.9' }}>
              {t?.brand?.tagline || 'Building the future through SUSTAINABLE design, intelligent ventures, and infinite thinking.'}
            </p>
            
            {/* Quick Contact */}
            <div className="space-y-3">
              <button 
                onClick={handleContactClick}
                className="inline-flex items-center gap-2 text-xs font-medium uppercase transition-colors"
                style={{ color: charcoal, letterSpacing: '0.12em' }}
                onMouseEnter={e => { e.currentTarget.style.color = gold; }}
                onMouseLeave={e => { e.currentTarget.style.color = charcoal; }}
              >
                <Mail className="w-4 h-4" />
                {t?.brand?.contact || 'Get in touch'}
              </button>
              <div className="flex items-center gap-2 text-sm font-light" style={{ color: tertiary }}>
                <Globe className="w-4 h-4" />
                {t?.brand?.studio || 'Digital Studio'}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-medium uppercase mb-5" style={{ color: gold, letterSpacing: '0.18em' }}>{t?.links?.title || 'Navigate'}</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              {quickLinks.map(({ name, slug }) => (
                <button 
                  key={slug} 
                  onClick={() => setCurrentPage(slug)}
                  className="text-left text-sm font-light transition-colors duration-300"
                  style={{ color: currentPage === slug ? charcoal : secondary }}
                  onMouseEnter={e => { e.currentTarget.style.color = charcoal; }}
                  onMouseLeave={e => { e.currentTarget.style.color = currentPage === slug ? charcoal : secondary; }}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          {/* Social Links */}
          <div>
            <h4 className="text-xs font-medium uppercase mb-5" style={{ color: gold, letterSpacing: '0.18em' }}>{t?.social?.title || 'Connect'}</h4>
            <div className="flex flex-wrap gap-3 mb-6">
              {[
                { icon: Twitter, label: 'Twitter', url: 'https://x.com/zeushernandez01' },
                { icon: Linkedin, label: 'LinkedIn', url: 'https://www.linkedin.com/in/zeus-hernandez/' },
                { icon: Youtube, label: 'YouTube', url: 'https://www.youtube.com/@thezeushernandez' },
                { icon: Instagram, label: 'Instagram', url: 'https://www.instagram.com/thezeushernandez/' },
                { icon: MessageCircle, label: 'WhatsApp', url: 'https://wa.me/+17076998732' },
              ].map(({ icon: Icon, label, url }) => (
                <a 
                  key={label} 
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
                  style={{ border: `1px solid ${borderRest}`, color: charcoal }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = gold; e.currentTarget.style.color = gold; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = borderRest; e.currentTarget.style.color = charcoal; }}
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="pt-8" style={{ borderTop: `1px solid ${borderRest}` }}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4 text-sm">
              <p className="font-light" style={{ color: tertiary }}>
                © {currentYear} Zeus Hernandez. {t?.bottom?.rights || 'All rights reserved.'}
              </p>
            </div>
            
            <div className="flex items-center gap-2 text-sm font-light" style={{ color: tertiary }}>
              <span>{t?.bottom?.madeWith || 'Made with'}</span>
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: gold }}></div>
              <span>{t?.bottom?.infinite || 'and infinite thinking'}</span>
            </div>
          </div>

          {/* Professional Stamp - Created by SwiftDev */}
          <div className="flex justify-center mt-8">
            <a
              href="https://swiftdev.vip/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#C8C6C2', fontSize: '0.7rem', letterSpacing: '0.08em', textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#9A9690')}
              onMouseLeave={e => (e.currentTarget.style.color = '#C8C6C2')}
            >
              crafted by SwiftDev
            </a>
          </div>
        </div>
      </div>

    </footer>
  );
};

export default Footer;
